import React, { useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Repeat, ShoppingCart, Tag, Eye } from "lucide-react";
import { api, formatCrypto, formatFiat } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import WithdrawalProgressModal from "@/components/WithdrawalProgressModal";

const ICONS = {
  deposit: ArrowDownLeft,
  withdraw: ArrowUpRight,
  convert: Repeat,
  buy: ShoppingCart,
  sell: Tag,
};

export default function Transactions() {
  const { t } = useI18n();
  const [txs, setTxs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [progressTxId, setProgressTxId] = useState(null);

  useEffect(() => {
    api.get("/transactions").then((r) => setTxs(r.data.transactions));
  }, []);

  const TYPE_LABEL = {
    deposit: t("tx.t_deposit"),
    withdraw: t("tx.t_withdraw"),
    convert: t("tx.t_convert"),
    buy: t("tx.t_buy"),
    sell: t("tx.t_sell"),
  };

  const STATUS_LABEL = {
    pending: t("tx.st_pending"),
    completed: t("tx.st_completed"),
    rejected: t("tx.st_rejected"),
    failed: t("tx.st_failed"),
  };
  const STATUS_STYLE = {
    pending: "text-[#D19B54] border-[#D19B54]/40 bg-[#D19B54]/10",
    completed: "text-[#7C9C69] border-[#7C9C69]/40 bg-[#7C9C69]/10",
    rejected: "text-[#C75C5C] border-[#C75C5C]/40 bg-[#C75C5C]/10",
    failed: "text-[#C75C5C] border-[#C75C5C]/40 bg-[#C75C5C]/10",
  };

  const FILTERS = [
    { key: "all", label: t("tx.f_all") },
    { key: "deposit", label: t("tx.f_deposit") },
    { key: "withdraw", label: t("tx.f_withdraw") },
    { key: "buy", label: t("tx.f_buy") },
    { key: "sell", label: t("tx.f_sell") },
    { key: "convert", label: t("tx.f_convert") },
  ];

  const filtered = filter === "all" ? txs : txs.filter((t) => t.type === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#6E7587]">{t("tx.eyebrow")}</div>
          <h1 className="font-display text-3xl font-bold mt-1">{t("tx.title")}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border rounded-sm transition-colors ${
                filter === f.key
                  ? "border-[#7C5CFF] text-[#7C5CFF]"
                  : "border-[#2A2E38] text-[#A3A9B8] hover:text-[#F4F1EB]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-[#2A2E38] bg-[#141720]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
              <tr className="border-b border-[#2A2E38]">
                <th className="text-left px-6 py-3 font-normal">{t("tx.col_type")}</th>
                <th className="text-left px-6 py-3 font-normal">{t("tx.col_asset")}</th>
                <th className="text-right px-6 py-3 font-normal">{t("tx.col_amount")}</th>
                <th className="text-left px-6 py-3 font-normal">{t("tx.col_status")}</th>
                <th className="text-left px-6 py-3 font-normal hidden md:table-cell">{t("tx.col_note")}</th>
                <th className="text-right px-6 py-3 font-normal">{t("tx.col_when")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tx) => {
                const Icon = ICONS[tx.type] || Repeat;
                const positive = tx.type === "deposit" || tx.type === "buy";
                const status = tx.status || "completed";
                return (
                  <tr key={tx.id} data-testid={`tx-row-${tx.id}`} className="border-b border-[#2A2E38] hover:bg-[#1C202B]">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Icon size={14} className={positive ? "text-[#7C9C69]" : "text-[#C75C5C]"} />
                        <span className="text-sm">{TYPE_LABEL[tx.type] || tx.type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-sm">
                      {tx.asset}
                      {tx.to_asset && <span className="text-[#6E7587]"> → {tx.to_asset}</span>}
                    </td>
                    <td className="px-6 py-4 text-right font-mono">
                      {tx.kind === "fiat"
                        ? formatFiat(tx.amount, tx.asset)
                        : formatCrypto(tx.amount)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        data-testid={`tx-status-${tx.id}`}
                        className={`inline-flex items-center text-[10px] tracking-[0.2em] uppercase px-2 py-1 border rounded-sm font-mono ${STATUS_STYLE[status] || STATUS_STYLE.completed}`}
                      >
                        {STATUS_LABEL[status] || status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-[#A3A9B8] hidden md:table-cell">{tx.note}</td>
                    <td className="px-6 py-4 text-right text-xs text-[#6E7587] font-mono">
                      <div className="flex items-center justify-end gap-2">
                        <span>{new Date(tx.created_at).toLocaleString()}</span>
                        {tx.type === "withdraw" && (
                          <button
                            data-testid={`tx-progress-${tx.id}`}
                            onClick={() => setProgressTxId(tx.id)}
                            className="p-1.5 border border-[#2A2E38] rounded-sm text-[#A3A9B8] hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors"
                            title={t("tx.view_progress")}
                          >
                            <Eye size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#6E7587]">{t("tx.no_transactions")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <WithdrawalProgressModal txId={progressTxId} onClose={() => setProgressTxId(null)} />
    </div>
  );
}
